import * as React from 'react';
import { useSelector } from 'react-redux';

import { ImageAsDocumentMessage } from '../../components/ImageAsDocumentMessage';
import { currentOpenChatIDSelector } from '../../selectors';
import { TPendingBlobFile } from '../../types/file';
import { TImageFileAsDocument } from '../../types/messageFiles';
import { formatBytes } from '../../utils/formatBytes';
import { getFileSrc } from '../../utils/getFileSrc';
import { ChatImageContainer } from '../ChatImageContainer';
import { ChatImageDraftContainer } from '../ChatImageDraftContainer';

type TImageAsDocumentMessageProps = {
  isOutcoming: boolean;
  isMessageLoading: boolean;
  isMessageFailed: boolean;
  imageAsDocumentDraft: TPendingBlobFile | null;
  imageAsDocument: TImageFileAsDocument | null;
  messageId: string;
};

export const ImageAsDocumentMessageContainer: React.FC<TImageAsDocumentMessageProps> = ({
  isOutcoming,
  isMessageLoading,
  isMessageFailed,
  imageAsDocumentDraft,
  imageAsDocument,
  messageId,
}) => {
  const chatId = useSelector(currentOpenChatIDSelector);

  const imageNode = React.useMemo(() => {
    if (imageAsDocumentDraft) {
      return (
        <ChatImageDraftContainer
          file={imageAsDocumentDraft.file}
          messageId={messageId}
          cancellationToken={imageAsDocumentDraft.cancellationToken}
          isMessageLoading={isMessageLoading}
          isMessageFailed={isMessageFailed}
        />
      );
    }

    return <ChatImageContainer image={imageAsDocument as TImageFileAsDocument} withSave />;
  }, [imageAsDocument, imageAsDocumentDraft, isMessageFailed, isMessageLoading, messageId]);

  const src = React.useMemo(() => {
    if (imageAsDocument?.uid) {
      return getFileSrc(imageAsDocument.uid, chatId);
    }

    return null;
  }, [chatId, imageAsDocument?.uid]);

  const name = React.useMemo(() => {
    return imageAsDocument?.name || imageAsDocumentDraft?.file?.name || '';
  }, [imageAsDocument?.name, imageAsDocumentDraft?.file?.name]);

  const size = React.useMemo(() => {
    const sizeNumber = imageAsDocument?.size || imageAsDocumentDraft?.file?.size;

    return formatBytes(sizeNumber);
  }, [imageAsDocument?.size, imageAsDocumentDraft?.file?.size]);

  if (!imageAsDocumentDraft && !imageAsDocument) {
    return null;
  }

  return <ImageAsDocumentMessage imageNode={imageNode} name={name} size={size} src={src} isOutcoming={isOutcoming} />;
};
